import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Contract for the Settings → Approvals admin UI. Lets org admins configure
 * approval levels and rules for both the discount and order systems through
 * a single, system-parameterized API.
 *
 * Two intentional shape decisions:
 *
 * 1. Conditions are exposed in flat `{ field, operator, value }` form to the
 *    frontend, not the MongoDB-style `{ "order.totalAmount": { "$gt": 50000 } }`
 *    form the rule engine reads. Handlers translate between the two so the
 *    rule engine stays untouched and the UI stays simple. v1 supports a single
 *    condition per rule; the array shape leaves room for future AND-composition.
 *
 * 2. `system: "discount" | "order"` discriminates everywhere. The two systems
 *    have separate tables and slightly different fields (only discount levels
 *    have `productCategories`), so the contract reflects that asymmetry rather
 *    than pretending a unified shape exists.
 */

export const approvalSystemSchema = z.enum(["discount", "order"]);
export type ApprovalSystem = z.infer<typeof approvalSystemSchema>;

// ─── Conditions (flat form for the UI) ──────────────────────────────────────

export const conditionFieldSchema = z.enum(["order.totalAmount"]);

export const conditionOperatorSchema = z.enum([
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "in",
  "nin",
]);

/**
 * A single condition. Scalar operators (`eq`, `gt`, etc.) carry a `number`;
 * list operators (`in`, `nin`) carry `number[]`. Validated by the handler
 * before persistence — invalid pairings are rejected with a 400.
 */
export const ruleConditionSchema = z.object({
  field: conditionFieldSchema,
  operator: conditionOperatorSchema,
  value: z.union([z.number(), z.array(z.number())]),
});

// ─── Levels ─────────────────────────────────────────────────────────────────

export const approvalLevelSchema = z.object({
  id: z.string().uuid(),
  level: z.number().int().positive(),
  name: z.string().min(1).max(255),
  /**
   * Discount-only. Empty array means "applies to every approval". Always
   * absent on order-system levels — the schema is a union to keep the field
   * presence load-bearing.
   */
  productCategories: z.array(z.string()).optional(),
});

export type ApprovalLevelDTO = z.infer<typeof approvalLevelSchema>;

const createLevelInput = z.object({
  system: approvalSystemSchema,
  name: z.string().min(1).max(255),
  productCategories: z.array(z.string()).optional(),
});

const updateLevelInput = z.object({
  system: approvalSystemSchema,
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
  productCategories: z.array(z.string()).optional(),
});

const deleteLevelInput = z.object({
  system: approvalSystemSchema,
  id: z.string().uuid(),
});

const listLevelsInput = z.object({ system: approvalSystemSchema });

// ─── Rules ──────────────────────────────────────────────────────────────────

export const approvalRuleSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
  priority: z.number().int().positive(),
  isActive: z.boolean(),
  requiredApprovalLevels: z.number().int().positive(),
  conditions: z.array(ruleConditionSchema).min(1),
});

export type ApprovalRuleDTO = z.infer<typeof approvalRuleSchema>;

const createRuleInput = z.object({
  system: approvalSystemSchema,
  name: z.string().min(1).max(255),
  isActive: z.boolean(),
  requiredApprovalLevels: z.number().int().positive(),
  conditions: z.array(ruleConditionSchema).min(1).max(1),
});

const updateRuleInput = z.object({
  system: approvalSystemSchema,
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
  isActive: z.boolean(),
  requiredApprovalLevels: z.number().int().positive(),
  conditions: z.array(ruleConditionSchema).min(1).max(1),
});

const deleteRuleInput = z.object({
  system: approvalSystemSchema,
  id: z.string().uuid(),
});

const listRulesInput = z.object({ system: approvalSystemSchema });

const reorderRulesInput = z.object({
  system: approvalSystemSchema,
  /** Rule IDs in their new order; index 0 becomes priority 1. */
  orderedIds: z.array(z.string().uuid()).min(1),
});

const toggleRuleActiveInput = z.object({
  system: approvalSystemSchema,
  id: z.string().uuid(),
  isActive: z.boolean(),
});

// ─── Approver assignments ──────────────────────────────────────────────────
//
// An assignment binds a user to a level for a specific customer or for every
// customer in the org (`customerId: null`). The DTO denormalizes display
// fields (level name + number, customer name, user name) so the list view
// can render without N+1 reads from the frontend.

export const approvalAssignmentSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  userName: z.string(),
  /** The user's primary email — shown alongside the override so admins can see
   * the default routing target. Null when the user has no primary email. */
  userPrimaryEmail: z.string().nullable(),
  /** When set, approval emails for this assignment route here instead of the
   * user's primary email. Null means "use the primary email." */
  emailOverride: z.string().nullable(),
  levelId: z.string().uuid(),
  levelNumber: z.number().int().positive(),
  levelName: z.string(),
  /** Null = the assignment applies to every customer in the organization. */
  customerId: z.string().uuid().nullable(),
  /** Null when `customerId` is null. */
  customerName: z.string().nullable(),
});

export type ApprovalAssignmentDTO = z.infer<typeof approvalAssignmentSchema>;

const listAssignmentsInput = z.object({ system: approvalSystemSchema });

const createAssignmentInput = z.object({
  system: approvalSystemSchema,
  userId: z.string().uuid(),
  levelId: z.string().uuid(),
  /** Null grants authority for every customer in the organization. */
  customerId: z.string().uuid().nullable(),
  /** Optional. When set, approval emails route here instead of the user's
   * primary email. Empty string is treated as null. */
  emailOverride: z.string().email().nullable().optional(),
});

const updateAssignmentInput = z.object({
  system: approvalSystemSchema,
  id: z.string().uuid(),
  /** Null clears the override so notifications fall back to the primary email. */
  emailOverride: z.string().email().nullable(),
});

const deleteAssignmentInput = z.object({
  system: approvalSystemSchema,
  id: z.string().uuid(),
});

// ─── Procedures ─────────────────────────────────────────────────────────────

export const approvalConfigContracts = {
  listLevels: oc
    .input(listLevelsInput)
    .output(z.object({ levels: z.array(approvalLevelSchema) }))
    .route({
      method: "GET",
      summary: "List approval levels",
      description:
        "Returns the configured approval levels for the discount or order approval system, including product categories on discount levels.",
    }),

  createLevel: oc.input(createLevelInput).output(approvalLevelSchema).route({
    method: "POST",
    summary: "Create an approval level",
    description:
      "Adds a discount or order approval level with a name (and product categories for discounts); the level number is assigned automatically as the next slot.",
  }),

  updateLevel: oc.input(updateLevelInput).output(approvalLevelSchema).route({
    method: "POST",
    summary: "Rename an approval level",
    description:
      "Updates a discount or order approval level's name, and its product categories on discount levels. The level number cannot change.",
  }),

  deleteLevel: oc
    .input(deleteLevelInput)
    .output(z.object({ success: z.literal(true) }))
    .route({
      method: "POST",
      summary: "Delete an approval level",
      description:
        "Removes a discount or order approval level; refused if any rule requires that many approvals or a pending approval references it.",
    }),

  listRules: oc
    .input(listRulesInput)
    .output(z.object({ rules: z.array(approvalRuleSchema) }))
    .route({
      method: "GET",
      summary: "List approval rules",
      description:
        "Returns discount or order approval rules in priority order with their order-total condition, required approval levels, and active flag.",
    }),

  createRule: oc.input(createRuleInput).output(approvalRuleSchema).route({
    method: "POST",
    summary: "Create an approval rule",
    description:
      "Adds a discount or order approval rule with an order-total condition and required approval levels, placed at the lowest priority.",
  }),

  updateRule: oc.input(updateRuleInput).output(approvalRuleSchema).route({
    method: "POST",
    summary: "Update an approval rule",
    description:
      "Changes an approval rule's name, order-total condition, required approval levels, and active flag for the discount or order system.",
  }),

  deleteRule: oc
    .input(deleteRuleInput)
    .output(z.object({ success: z.literal(true) }))
    .route({
      method: "POST",
      summary: "Delete an approval rule",
      description:
        "Removes a discount or order approval rule; refused if any pending approval was triggered by it.",
    }),

  reorderRules: oc
    .input(reorderRulesInput)
    .output(z.object({ rules: z.array(approvalRuleSchema) }))
    .route({
      method: "POST",
      summary: "Reorder approval rule priorities",
      description:
        "Rewrites rule priorities to match the given ordered rule IDs, first becoming top priority, in one transaction. Returns the reordered rules.",
    }),

  toggleRuleActive: oc
    .input(toggleRuleActiveInput)
    .output(approvalRuleSchema)
    .route({
      method: "POST",
      summary: "Enable or disable an approval rule",
      description:
        "Sets a discount or order approval rule active or inactive; inactive rules stop triggering approvals.",
    }),

  listAssignments: oc
    .input(listAssignmentsInput)
    .output(z.object({ assignments: z.array(approvalAssignmentSchema) }))
    .route({
      method: "GET",
      summary: "List approver assignments",
      description:
        "Returns which users approve at which level, per customer or org-wide, for the discount or order system, with names and email overrides.",
    }),

  createAssignment: oc
    .input(createAssignmentInput)
    .output(approvalAssignmentSchema)
    .route({
      method: "POST",
      summary: "Assign a user as approver",
      description:
        "Grants a user approval authority at a level for one customer or all customers, with optional notification email override. Users cannot assign themselves.",
    }),

  updateAssignment: oc
    .input(updateAssignmentInput)
    .output(approvalAssignmentSchema)
    .route({
      method: "POST",
      summary: "Set approver notification email override",
      description:
        "Sets or clears the email address that receives approval notifications for an approver assignment; null falls back to the user's primary email.",
    }),

  deleteAssignment: oc
    .input(deleteAssignmentInput)
    .output(z.object({ success: z.literal(true) }))
    .route({
      method: "POST",
      summary: "Remove an approver assignment",
      description:
        "Revokes a user's approval authority at a level for the discount or order system.",
    }),
} as const;
